import { DetailBar, Icon, InputNumber, Number, Text } from "components/common"
import { FeatureChargesTable, Separator } from "components/custom"
import { t } from "i18next"
import { useMemo } from "react"
import { joinBytItems, localisedValue, removeDuplicates } from "utils"
import { dateTimeFunction } from "utils/date"
import { RiFileCopyLine } from "react-icons/ri"
import theme from "utils/themeVars"
import { Row } from "antd"
import { NAME_SPACE } from "constants/app-paths"
import cs from "./index.module.less"
import { Form } from "antd"
import { numberProps } from "features/leads/components/addSalesModal/utils/constants"
import InvoiceDropDown from "../invoiceDropDown"
import {
  customValidatorForCDP,
  customVASComponent,
  DefaultProjectDisplay,
  ProjectInfoDisplay,
  VASBreakdownLabel,
} from "features/leads/components/addSalesModal/utils"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"

const ProjectDetailBar = ({ rules }) => {
  const {
    addSalesApis,
    stateManager,
    updateStateManager,
    form,
    isEditable,
    updateIsEditable,
    formWatchers,
    lead,
    vasEnabled,
    optionalSelections,
    setOptionalSelections,
  } = useAddSalesContext()

  const { project, unit, cdpCommitment } = formWatchers

  const { paymentList, leadSalesInfo } = addSalesApis

  const vasFeesBreakdown = useMemo(() => {
    if (!leadSalesInfo && !project) return []
    if (leadSalesInfo?.isVasRemaining) {
      return leadSalesInfo?.contracts?.[0]?.snapshot?.vasFees ?? []
    }
    return project?.ProjectFinancial?.vasFees ?? []
  }, [leadSalesInfo, project])

  const getTableData = useMemo(() => {
    return leadSalesInfo?.features || unit?.FeatureCharges
  }, [leadSalesInfo, unit])

  const columns = useMemo(
    () => [
      { title: t("Feature"), dataIndex: "Feature" },
      {
        title: (
          <Number
            tooltip
            value={
              leadSalesInfo?.totalFeatureCharges ||
              stateManager?.featureTable?.selectedPrices ||
              unit?.totalFixedFeatureCharges
            }
            {...numberProps}
          />
        ),
        dataIndex: "Amount",
        width: 100,
        className: "text-right",
      },
    ],
    [stateManager?.featureTable?.selectedPrices, unit, t, leadSalesInfo]
  )

  const getSelectedRowKeys = useMemo(() => {
    const fixedKeys = getTableData?.filter(record => record?.fixed)?.map(record => record.key) || []
    return removeDuplicates((stateManager?.featureTable?.selectedRowKeys || [])?.concat(fixedKeys))
  }, [getTableData, stateManager?.featureTable?.selectedRowKeys])

  const projectInfo = useMemo(
    () =>
      leadSalesInfo?.projectName
        ? joinBytItems(
            [
              leadSalesInfo?.projectUrl ? (
                <a href={leadSalesInfo?.projectUrl} target="_blank" rel="noreferrer" style={{ color: "currentColor" }}>
                  {leadSalesInfo?.projectName}
                </a>
              ) : (
                leadSalesInfo?.projectName
              ),
              `${leadSalesInfo?.unitName ?? ""}${leadSalesInfo?.type && `(${leadSalesInfo?.type})`}`,
            ],
            <Separator />
          )
        : [],
    [leadSalesInfo]
  )

  const saleDetail = useMemo(() => {
    const clientDetail = {
      label: <Text className="semiBold">{t("Client")}</Text>,
      value: (
        <Text className="semiBold">
          {localisedValue({ object: leadSalesInfo?.inquiry?.Client, key: "name" }) ??
            localisedValue({ object: lead?.Client, key: "name" })}
        </Text>
      ),
    }
    const details = [
      clientDetail,
      {
        label: <Text className="semiBold">{t("Lead ID")}</Text>,
        value: (
          <Text className="semiBold" type="info">
            {leadSalesInfo?.inquiry?.id ?? lead?.id}
          </Text>
        ),
      },
      leadSalesInfo?.contractId
        ? {
            label: <Text className="semiBold">{t("Contract ID")}</Text>,
            value: (
              <div>
                <Text className="semiBold">
                  <a
                    href={`${window.location.origin}/${NAME_SPACE[0]}/contracts/?contractId=${
                      leadSalesInfo?.isVasRemaining ? leadSalesInfo?.vasContractId : leadSalesInfo?.contractId
                    }`}
                  >
                    {leadSalesInfo?.isVasRemaining ? leadSalesInfo?.vasContractId : leadSalesInfo?.contractId}
                  </a>
                </Text>
                {leadSalesInfo?.contractExpiry && !leadSalesInfo?.isVasRemaining && (
                  <Text className="small semiBold" type="danger">
                    ({t("Expiry")} {dateTimeFunction({ date: leadSalesInfo?.contractExpiry, format: "Do MMM" })})
                  </Text>
                )}
              </div>
            ),
          }
        : null,
    ]
    return details.filter(detail => detail?.value)
  }, [leadSalesInfo, t, lead])

  const salesSteps = useMemo(() => {
    return [
      {
        label: <Text className="semiBold">{t("Unit Price")}</Text>,
        data: <Number tooltip {...numberProps} value={Math.round(stateManager?.unitPrice)} />,
      },
      ((unit && unit?.FeatureCharges?.length !== 0) || (leadSalesInfo && leadSalesInfo?.features?.length)) && {
        label: <Text className="semiBold">{t("Features")}</Text>,
        data: (
          <FeatureChargesTable
            tableData={getTableData}
            columns={columns || []}
            selectedRowKeys={getSelectedRowKeys}
            scroll={false}
            onChange={(selectedRowKeys, selectedRows) => {
              let selectedFeaturePrices = 0
              selectedRows?.forEach(item => {
                selectedFeaturePrices +=
                  item?.amountType === "fixed" ? item?.amount : (item?.amount / 100) * stateManager?.unitPrice
              })
              const baseDealPrice = unit?.unitPrice || 0
              const newDealPrice = baseDealPrice + selectedFeaturePrices
              updateStateManager({
                dealPrice: newDealPrice,
                featureTable: {
                  selectedRowKeys,
                  selectedPrices: selectedFeaturePrices,
                  features: selectedRows,
                },
              })
            }}
            tableClass={cs.tableTransparent}
          />
        ),
      },
      leadSalesInfo &&
        leadSalesInfo?.hasMultipleSalesTasks && {
          label: <Text className="semiBold">{t("Unit Discount")}</Text>,
          data: <Number {...numberProps} value={stateManager?.discountAmount} />,
        },
      {
        label: <Text className="semiBold">{t("Booking Price")}</Text>,
        data: <Number {...numberProps} tooltip={stateManager?.dealPrice} value={stateManager?.dealPrice} />,
      },
      ((vasEnabled && leadSalesInfo?.isFirstSalesTask) || (!!vasEnabled && leadSalesInfo?.isVasRemaining)) && {
        label: VASBreakdownLabel({ vasFees: vasFeesBreakdown, setOptionalSelections }),
        data: leadSalesInfo?.isVasRemaining
          ? leadSalesInfo?.contracts?.[0]?.snapshot?.vasFees
          : project?.ProjectFinancial?.vasFees,
        isComponent: true,
      },
      leadSalesInfo &&
        leadSalesInfo?.hasMultipleSalesTasks && {
          label: <Text className="semiBold">{t("Paid Amount")}</Text>,
          data: (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Number {...numberProps} value={leadSalesInfo?.paidAmount} />
              <Number
                isStringOnly={true}
                digitAfterFraction={0}
                value={(leadSalesInfo?.paidAmount / (leadSalesInfo?.dealPrice || 1)) * 100}
                wrapValue={value => ` (${value}%)`}
              />
              <span
                style={{ cursor: "pointer", color: theme["primary-color"] }}
                onClick={() => navigator.clipboard.writeText(leadSalesInfo?.paidAmount)}
              >
                {leadSalesInfo?.paidAmount && <RiFileCopyLine />}
              </span>
            </div>
          ),
        },
      leadSalesInfo?.isFirstSalesTask && {
        label: (
          <Text style={{ marginTop: 6 }}>
            <span className="semiBold">{t("CDP Commitment")}</span>{" "}
            {`${t("(must be between")} ${project?.ProjectFinancial?.completeDownPayment}% ${t("and")} 100%)`}
          </Text>
        ),
        data: (
          <Form.Item
            name="cdpCommitment"
            initialValue={project?.ProjectFinancial?.completeDownPayment || 0}
            rules={[
              ...rules,
              {
                validator: async (_, value) => {
                  return customValidatorForCDP(value, project)
                },
              },
            ]}
            className="mbe-0 text-right"
          >
            <InputNumber
              size="small"
              isStringOnly={true}
              digitAfterFraction={0}
              addonAfter={
                <Icon
                  icon="BiEdit"
                  style={{ cursor: "pointer", color: "#9d9d9d" }}
                  onClick={() => updateIsEditable(true)}
                />
              }
              disabled={!isEditable}
              style={{ width: 100, "--ant-input-number-active-bg": "#fff", marginRight: 0 }}
              value={cdpCommitment}
              onChange={value => {
                form.setFieldsValue({ cdpCommitment: value })
              }}
            />
          </Form.Item>
        ),
      },
      {
        label: <Text className="semiBold">{t("Due Amount")}</Text>,
        data: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Number
              {...numberProps}
              tooltip
              value={leadSalesInfo?.remainComDowPayment || ((stateManager?.dealPrice || 0) * cdpCommitment) / 100}
            />
            <Number
              isStringOnly={true}
              digitAfterFraction={0}
              value={
                leadSalesInfo?.remainComDowPayment
                  ? (leadSalesInfo?.remainComDowPayment / (leadSalesInfo?.dealPrice || 1)) * 100
                  : cdpCommitment || 0
              }
              wrapValue={value => ` (${value}%)`}
            />
            <span
              style={{ cursor: "pointer", color: theme["primary-color"] }}
              onClick={() =>
                navigator.clipboard.writeText(
                  leadSalesInfo?.remainComDowPayment || ((stateManager?.dealPrice || 0) * cdpCommitment) / 100
                )
              }
            >
              <RiFileCopyLine color={project?.ProjectFinancial?.vasFeeEnabled && theme["accent-color"]} />
            </span>
          </div>
        ),
      },
    ].filter(Boolean)
  }, [leadSalesInfo, t, unit, cdpCommitment, project, rules, form, stateManager?.unitPrice])

  const renderDetailHeader = () => {
    return (
      <Row justify="space-between">
        {projectInfo?.length > 0 ? (
          <ProjectInfoDisplay projectInfo={projectInfo} />
        ) : (
          <DefaultProjectDisplay project={project} unit={unit} />
        )}
        <InvoiceDropDown
          paymentList={paymentList}
          disabled={leadSalesInfo?.contracts[0] ? false : true}
          buttonColor={
            (vasEnabled && leadSalesInfo?.isFirstSalesTask) || (!!vasEnabled && leadSalesInfo?.isVasRemaining)
              ? theme["accent-color"]
              : undefined
          }
        />
      </Row>
    )
  }
  return (
    <>
      {(leadSalesInfo?.current >= 0 ||
        (leadSalesInfo?.contracts[0] && !leadSalesInfo?.current) ||
        (!leadSalesInfo?.contracts[0] && project && unit)) && (
        <DetailBar
          collapsible
          detailData={saleDetail}
          listData={salesSteps}
          detailContainerClass="flexYcenter"
          detailHeader={renderDetailHeader()}
          customComponent={customVASComponent(stateManager, leadSalesInfo, optionalSelections)}
          {...(((!!vasEnabled && leadSalesInfo?.isFirstSalesTask) ||
            (!!vasEnabled && leadSalesInfo?.isVasRemaining)) && {
            className: cs.collapseSecondary,
            bgColor: "#EFF8FC",
            borderColor: theme["accent-color"],
          })}
        />
      )}
    </>
  )
}
export default ProjectDetailBar
